"use client";

// lib/experiments.ts — client-side helper for PostHog A/B experiments.
//
// IMPORTANT boundary: PostHog feature flags are reserved for EXPERIMENTS in this
// codebase. Ordinary app feature toggles use the homegrown DB-backed system
// (lib/feature-flags.ts + /admin/feature-flags) — see docs/INFRASTRUCTURE.md.
//
// useExperimentVariant centralises the flag-read pattern that was inlined in
// components/funnel/funnel-cta-link.tsx so the next experiment is a one-liner:
//   - returns `fallback` until posthog-js has loaded (SSR / first-paint safe),
//   - re-evaluates when flags arrive (posthog.onFeatureFlags),
//   - supports a dev-only `?<overrideParam>=` query override for manual QA.
//
// posthog.getFeatureFlag() automatically fires `$feature_flag_called`, which is
// the experiment EXPOSURE event — so exposure is tracked with no extra code as
// long as the flag exists in PostHog.

import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";

export interface UseExperimentOptions<V extends string> {
  /** The known variant keys for this experiment (e.g. ["control", "variant"]). */
  variants: readonly V[];
  /** Value returned before flags load and when the flag is missing/unknown. */
  fallback: V;
  /** Dev-only query param that overrides the variant for manual QA (e.g. "ab"). */
  overrideParam?: string;
}

export function useExperimentVariant<V extends string>(
  flagKey: string,
  { variants, fallback, overrideParam }: UseExperimentOptions<V>,
): { variant: V; ready: boolean } {
  const readOverride = (): V | null => {
    if (
      typeof window === "undefined" ||
      process.env.NODE_ENV === "production" ||
      !overrideParam
    ) {
      return null;
    }
    const value = new URLSearchParams(window.location.search).get(
      overrideParam,
    );
    return value && (variants as readonly string[]).includes(value)
      ? (value as V)
      : null;
  };

  const [variant, setVariant] = useState<V>(() => readOverride() ?? fallback);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const override = readOverride();
    if (override) {
      setVariant(override);
      setReady(true);
      return;
    }

    let unsubscribe: (() => void) | undefined;
    const apply = () => {
      const raw = posthog.getFeatureFlag(flagKey);
      setVariant(
        typeof raw === "string" && (variants as readonly string[]).includes(raw)
          ? (raw as V)
          : fallback,
      );
      setReady(true);
    };

    const subscribe = () => {
      if (
        unsubscribe ||
        !posthog.__loaded ||
        readAnalyticsConsent() !== "accepted"
      ) {
        return;
      }
      apply();
      unsubscribe = posthog.onFeatureFlags(apply);
    };

    const reconcile = () => {
      if (readAnalyticsConsent() === "accepted") {
        subscribe();
        return;
      }
      unsubscribe?.();
      unsubscribe = undefined;
      setVariant(fallback);
      setReady(false);
    };

    const onConsentChange = () => {
      // The provider applies the cookie to PostHog synchronously first.
      queueMicrotask(reconcile);
    };

    // Consent may be undecided forever. Keep the call-site's deterministic
    // fallback and avoid touching the SDK until both conditions are true.
    reconcile();
    window.addEventListener(POSTHOG_BROWSER_INITIALIZED_EVENT, reconcile);
    const unsubscribeConsent = subscribeToBrowserConsentCategory(
      "analytics",
      onConsentChange,
    );

    return () => {
      window.removeEventListener(POSTHOG_BROWSER_INITIALIZED_EVENT, reconcile);
      unsubscribeConsent();
      unsubscribe?.();
    };
    // readOverride/variants/fallback are stable per call site; key off flagKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagKey]);

  return { variant, ready };
}
